import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import useImageAspectRatio from '_utils/hooks/useImageAspectRatio';
import { smallestMargin, titleFontSize, smallestFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CacheImage, HeimeText } from 'Components';
import { ApiImage, FileImage } from 'types/Base';

const ImagePairDisplay = ({ images }: { images: Array<ApiImage | FileImage> }) => {
    const { t } = useTranslation();
    const { theme } = useThemeContext();
    const firstImage = images[0];
    const { aspectRatio, handleLoad } = useImageAspectRatio(
        firstImage && 'blurhash' in firstImage ? (firstImage.blurhash ?? null) : null,
    );

    if (images.length === 0) {
        return null;
    }

    // Convert hex to rgba with 0.8 opacity
    const overlayColor = theme.black.replace(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, (_, hex) => {
        const r = parseInt(hex.length === 3 ? hex[0].repeat(2) : hex.substr(0, 2), 16);
        const g = parseInt(hex.length === 3 ? hex[1].repeat(2) : hex.substr(2, 2), 16);
        const b = parseInt(hex.length === 3 ? hex[2].repeat(2) : hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.8)`;
    });

    const remainingPhotosCount = images.length - 1; // Count includes the second image

    return (
        <View style={{ flexDirection: 'row', gap: smallestMargin, marginBottom: smallestMargin }}>
            <View style={{ flex: 1 }}>
                <CacheImage<ApiImage | FileImage>
                    source={images[0]}
                    resizeMode="cover"
                    style={{
                        width: '100%',
                        aspectRatio: aspectRatio || 1,
                    }}
                    onLoad={handleLoad}
                />
            </View>
            {images.length > 1 && (
                <View style={{ flex: 1, position: 'relative' }}>
                    <CacheImage<ApiImage | FileImage>
                        source={images[1]}
                        resizeMode="cover"
                        style={{
                            width: '100%',
                            aspectRatio: aspectRatio || 1,
                        }}
                    />
                    {remainingPhotosCount > 1 && (
                        <View
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: overlayColor,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <HeimeText
                                style={{
                                    color: 'white',
                                    fontSize: titleFontSize,
                                    fontWeight: 'bold',
                                }}
                            >
                                +{remainingPhotosCount}
                            </HeimeText>
                            <HeimeText
                                style={{
                                    color: 'white',
                                    fontSize: smallestFontSize,
                                    marginTop: smallestMargin / 2,
                                }}
                            >
                                {t('sharingAll:photos')}
                            </HeimeText>
                        </View>
                    )}
                </View>
            )}
        </View>
    );
};

export default ImagePairDisplay;
